<script setup lang="ts">
import RTOverviewSubmissionBox from "../RTOverviewSubmissionBox/RTOverviewSubmissionBox.vue";
import RTOverviewArticleTypesList from "../RTOverviewArticleTypesList/RTOverviewArticleTypesList.vue";
import RTOverviewBrakgroundFeeProgram from "./RTOverviewBrakgroundFeeProgram/RTOverviewBrakgroundFeeProgram.vue";
import type { IRTOverviewBackground } from "./IRTOverviewBackground";
import type { ISanitizeOptions } from "@frontiers-vue3/public-pages";

const props = defineProps<IRTOverviewBackground>();

const sanitize = inject("sanitize") as (
  html: string,
  options?: ISanitizeOptions
) => string;

const { getIsSubmitManuscriptEnabled } = useMain();
const { showInstitutionsBlock, isResearchTopicImportantNoteVisible } =
  useEnvironmentConfigStore().getConfig();
const terminology = useTerminologyStore();
const desc = ref(
  sanitize(props.researchTopic.description, {
    ALLOWED_TAGS: ["b", "strong", "a", "br", "img", "i", "em", "sub", "sup"]
  })
);

const rtKeywords = computed((): string => {
  if (!props.researchTopic.keywords) {
    return "";
  }
  return props.researchTopic.keywords.map((rtKey) => rtKey.title).join(", ");
});

const abstractSubmissionDeadline = computed((): Date => {
  return new Date(props.researchTopic.abstractSubmissionDeadline);
});

const manuscriptSubmissionDeadline = computed((): Date => {
  return new Date(props.researchTopic.manuscriptSubmissionDeadline);
});

const manuscriptSubmissionExtendedDeadline = computed((): Date => {
  return new Date(props.researchTopic.manuscriptSubmissionExtendedDeadline);
});

const displayFeeProgram = computed(() => {
  return showInstitutionsBlock && props.researchTopic.isOpenForSubmission;
});
</script>
<template>
  <section>
    <h2 class="Section__title">
      About this {{ terminology.getTerm("researchTopic") }}
    </h2>

    <!-- Submission box -->
    <RTOverviewSubmissionBox
      :abstract-submission-deadline="abstractSubmissionDeadline"
      :manuscript-submission-deadline="manuscriptSubmissionDeadline"
      :manuscript-submission-extended-deadline="
        manuscriptSubmissionExtendedDeadline
      "
      :created-date="new Date(researchTopic.createdDate)" />

    <div class="RTOverviewBackground">
      <h3 class="RTOverviewBackground__title">Background</h3>

      <div class="RTOverviewBackground__expandable">
        <!-- eslint-disable-next-line vue/no-v-html -- Content is sanitized on line 20 with allowed tags -->
        <p v-html="desc"></p>

        <div
          ref="expandableSecondaryRef"
          class="RTOverviewBackground__expandable__secondary">
          <div
            v-if="researchTopic.imageUrl"
            class="RTOverviewBackground__moreTextImg">
            <img
              :src="researchTopic.imageUrl"
              :alt="`${terminology.getTerm(
                'researchTopic'
              )} Research topic image`" />
          </div>

          <!-- Article types -->
          <RTOverviewArticleTypesList
            v-if="getIsSubmitManuscriptEnabled"
            :research-topic="researchTopic" />

          <!-- Fee program -->
          <RTOverviewBrakgroundFeeProgram v-if="displayFeeProgram" />

          <!-- Keywords & Important Note -->
          <div class="RTOverviewBackground__keywords">
            <p v-if="rtKeywords"><strong>Keywords: </strong>{{ rtKeywords }}</p>
            <p v-if="isResearchTopicImportantNoteVisible">
              <strong>Important note: </strong>
              All contributions to this
              {{ terminology.getTerm("researchTopic") }} must be within the
              scope of the section and journal to which they are submitted, as
              defined in their mission statements.
              {{ terminology.getTerm("tenantName") }} reserves the right to
              guide an out-of-scope manuscript to a more suitable section or
              journal at any stage of peer review.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style lang="scss">
.RTOverviewBackground {
  margin: 32px 0;

  .RTOverviewBackground__title {
    @include f-20-500;
    margin: 0 0 24px 0;
  }

  p {
    -webkit-line-clamp: unset;
    line-clamp: unset;
    margin-bottom: 0;
  }

  a {
    color: var(--maincolor50);
    font-weight: 700;

    &:hover {
      color: var(--maincolor70);
    }
  }

  b {
    font-weight: 500;
  }

  strong {
    font-weight: 700;
  }

  &__keywords {
    border-top: 1px solid $grey40;
    margin-top: 32px;

    p {
      margin-top: 32px;
      margin-bottom: 16px;
      @include f-12-300;
      strong {
        font-weight: 700;
      }
    }
  }

  img {
    height: auto;
  }

  &__moreTextImg {
    width: var(--main-text-img-size);
    height: var(--main-text-img-size);
    margin-top: 24px;
    position: relative;
    // opacity: 0;

    &--noLimit {
      opacity: 1;
    }

    @media #{$break-xs} {
      width: 100%;
      height: auto;
    }

    img {
      margin: 0;
      width: 100%;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
    }
  }

  .FeeProgram {
    margin: 56px 0;
  }
}
</style>
